import React, { useState } from 'react'
import { Popup } from 'semantic-ui-react'
import { useSourceMetricsWithTrends } from '../../../../hooks/useDataQuality'
import LoadingSkeleton from '../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../common/ErrorDisplay'
import SourceInfoCards from './source-detail/SourceInfoCards'
import CircularProgressMetric from './source-detail/CircularProgressMetric'
import FileMetricsGrid from './source-detail/FileMetricsGrid'
import FileDetailsView from './FileDetailsView'
import './SourceDetailView.css'

interface SourceDetailViewProps {
  source: any
  onBack: () => void
}

const SourceDetailView: React.FC<SourceDetailViewProps> = ({ source, onBack }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [showFileDetails, setShowFileDetails] = useState(false)

  // Agent descriptions mapping
  const agentDescriptions: { [key: string]: string } = {
    'Schema Mapping Agent': 'Analyzes CSV file structure and suggests canonical field mappings',
    'Client Table Mapping Agent': 'Maps canonical field names to PostgreSQL database schema',
    'Validation & Transformation Agent': 'Validates and transforms data based on plain-language rules',
    'Identity Resolution Agent': 'Performs identity matching and deduplication with hierarchical matching',
    'Conflict Resolution Agent': 'Resolves field-level conflicts when merging records from different sources',
    'Validation Agent': 'Validates and transforms data based on plain-language rules'
  }

  // Fetch source metrics with trends - Using profile-based approach
  const { data: metricsData, isLoading, error } = useSourceMetricsWithTrends(
    source?.source_id || null,
    {
      comparison_days: 7,
      include_detailed_trends: false,
      profile: 'complete'  // Includes all fields: error_breakdown, consumers, schema_fields, latest_file, file_health, file_sla_status, source_impact, error_rate_time_series
    }
  )

  // Helper to get metric values safely
  const getMetricValue = (value?: number, defaultValue: number = 0): number => {
    return value ?? defaultValue
  }

  // If showing file details, render the FileDetailsView component
  if (showFileDetails) {
    return (
      <FileDetailsView
        source={source}
        onBack={() => setShowFileDetails(false)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="source-detail-view">
        <div className="source-detail-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14 5L7 12L14 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="source-name">{source?.source_name}</span>
          </button>
        </div>
        <LoadingSkeleton variant="card" count={3} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="source-detail-view">
        <div className="source-detail-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14 5L7 12L14 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="source-name">{source?.source_name}</span>
          </button>
        </div>
        <ErrorDisplay
          error={error.message}
          onRetry={() => globalThis.location.reload()}
          title="Failed to Load Source Details"
        />
      </div>
    )
  }

  const trustScore = getMetricValue(metricsData?.metrics?.trust_score?.current_value, 0)

  // Extract file metadata from API
  const filename = metricsData?.latest_file?.original_filename || 'Unknown'
  const rawFileType = metricsData?.latest_file?.file_type || ''
  const fileType = rawFileType.includes('csv') ? 'CSV' :
                   rawFileType.includes('json') ? 'JSON' :
                   rawFileType.includes('xml') ? 'XML' :
                   rawFileType ? rawFileType.split('/').pop()?.toUpperCase() || 'Unknown' : 'Unknown'
  const protocol = metricsData?.latest_file?.protocol || 'Unknown'

  // Extract file health status
  const fileHealthStatus = metricsData?.file_health?.status || 'HEALTHY'
  const fileHealthBadge = fileHealthStatus === 'HEALTHY' ? 'healthy' :
                         fileHealthStatus === 'WARNING' ? 'warning' : 'critical'
  const fileHealthLabel = fileHealthStatus.charAt(0) + fileHealthStatus.slice(1).toLowerCase()

  // Extract error breakdown
  const missingValuesCount = metricsData?.error_breakdown?.errors_by_type?.missing_values || 0
  const duplicatesCount = metricsData?.error_breakdown?.errors_by_type?.duplicates || 0
  const formatIssuesCount = metricsData?.error_breakdown?.errors_by_type?.format_issues || 0
  const rangeViolationsCount = metricsData?.error_breakdown?.errors_by_type?.range_violations || 0
  const conflictsCount = metricsData?.error_breakdown?.errors_by_type?.conflicts || 0
  const schemaDriftChanges = metricsData?.schema_drift?.total_changes || 0

  // Extract consumers
  const consumers = metricsData?.used_by?.consumers || []

  // Extract schema fields
  const schemaFields = metricsData?.schema_overview?.fields || []

  // Record count
  const recordCount = metricsData?.summary?.total_records_processed ?? 0

  // Extract SLA status
  const slaStatus = metricsData?.file_sla_status?.status;
  let slaStatusBadge = 'no-sla'
  if (slaStatus === 'ON_TIME') {
    slaStatusBadge = 'on-time'
  } else if (slaStatus === 'AT_RISK') {
    slaStatusBadge = 'at-risk'
  } else if (slaStatus === 'BREACHED') {
    slaStatusBadge = 'breached'
  }
  let slaStatusLabel = 'No SLA configured'
  if (slaStatus === 'ON_TIME') {
    slaStatusLabel = 'On time'
  } else if (slaStatus === 'AT_RISK') {
    slaStatusLabel = 'At risk'
  } else if (slaStatus === 'BREACHED') {
    slaStatusLabel = 'Breached'
  }

  return (
    <div className="source-detail-view">
      {/* Header */}
      <div className="source-detail-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 5L7 12L14 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="8" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="source-name">{source?.source_name}</span>
        </button>
      </div>

      {/* Top Info Cards */}
      <SourceInfoCards metricsData={metricsData} source={source} />

      {/* File-Level Metrics Section */}
      <div className="file-level-metrics-section">
        <div className="metrics-section-header">
          <h3 className="metrics-section-title">File-Level Metrics</h3>
          <p className="metrics-section-subtitle">Detailed metrics and health status for each file</p>
        </div>

        <div className="file-metrics-content">
          {/* File Metrics Card */}
          <div className="file-metrics-card">
            {/* File Header */}
            <div className="file-card-header">
              <div className="file-info-left">
                <div className="file-icon-name">
                  <div className="filename-section">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                    <span className="filename">{filename}</span>
                  </div>
                  <div className="file-info-right">
                    <div className="trust-score-header">
                      <div className="trust-score-content">
                        <div className="trust-score-label">File Trust Score</div>
                        <div className="trust-score-value">
                          <span 
                            className="score-number"
                            style={{
                              color: trustScore > 95 ? '#22c55e' : trustScore >= 90 ? '#f59e0b' : '#ef4444'
                            }}
                          >
                            {trustScore.toFixed(0)}
                          </span>
                          <span className="score-total">/100</span>
                        </div>
                      </div>
                      <button
                        className="expand-collapse-btn"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="file-status-badges">
                  <span className={`status-badge ${fileHealthBadge}`}>{fileHealthLabel}</span>
                  <span className="status-badge file-type">{fileType}</span>
                  <span className="status-badge protocol">{protocol}</span>
                </div>
              </div>
            </div>

            {/* File Metrics Grid - Collapsible */}
            {isExpanded && (
              <>
                <div className="file-metrics-grid">
                  <div className="file-metric-item">
                    <div className="metric-label">Record Count</div>
                    <div className="metric-value">
                      {recordCount.toLocaleString()}
                    </div>
                  </div>
                  <div className="file-metric-item">
                    <div className="metric-label">Last Loaded</div>
                    <div className="metric-value">
                      {metricsData?.calculation_timestamp
                        ? (() => {
                            const date = new Date(metricsData.calculation_timestamp)
                            const year = date.getUTCFullYear()
                            const month = String(date.getUTCMonth() + 1).padStart(2, '0')
                            const day = String(date.getUTCDate()).padStart(2, '0')
                            const hours = String(date.getUTCHours()).padStart(2, '0')
                            const minutes = String(date.getUTCMinutes()).padStart(2, '0')
                            return `${year}-${month}-${day} ${hours}:${minutes} UTC`
                          })()
                        : 'N/A'
                      }
                    </div>
                  </div>
                  <div className="file-metric-item">
                    <div className="metric-label">SLA Status</div>
                    <div className="metric-value">
                      <span className={`sla-badge ${slaStatusBadge}`}>{slaStatusLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Detailed Metrics Section */}
                <div className="detailed-metrics-section">
                  <div className="metrics-header">
                    <h4 className="metrics-title">Metrics</h4>
                    <div className="metrics-legend">
                      <div className="legend-item">
                        <span className="legend-dot green"></span>
                        <span className="legend-text">&gt; 95%</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-dot yellow"></span>
                        <span className="legend-text">90-95%</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-dot red"></span>
                        <span className="legend-text">&lt; 90%</span>
                      </div>
                    </div>
                  </div>

                  <div className="metrics-content-grid">
                    <CircularProgressMetric score={trustScore} />
                    <FileMetricsGrid metricsData={metricsData} />
                  </div>
                </div>

                {/* Error Breakdown, Models & Schema Section */}
                <div className="additional-info-section">
                  {/* Error Breakdown */}
                  <div className="info-subsection">
                    <h4 className="info-subsection-title">Errors and Warnings</h4>
                    <div className="error-breakdown-items">
                      <div className="error-item">
                        <span className="error-label">Missing Values:</span>
                        <span className="error-value">{missingValuesCount}</span>
                      </div>
                      <div className="error-item">
                        <span className="error-label">Duplicates:</span>
                        <span className="error-value">{duplicatesCount}</span>
                      </div>
                      <div className="error-item">
                        <span className="error-label">Format Issues:</span>
                        <span className="error-value">{formatIssuesCount}</span>
                      </div>
                      <div className="error-item">
                        <span className="error-label">Range Violations:</span>
                        <span className="error-value">{rangeViolationsCount}</span>
                      </div>
                      <div className="error-item">
                        <span className="error-label">Conflicts:</span>
                        <span className="error-value">{conflictsCount}</span>
                      </div>
                      <div className="error-item">
                        <span className="error-label">Schema Drift:</span>
                        <span className="error-value">{schemaDriftChanges}</span>
                      </div>
                    </div>
                  </div>

                  {/* Used By Models & Systems */}
                  <div className="info-subsection">
                    <h4 className="info-subsection-title">Used By Models & Systems</h4>
                    <div className="models-tags">
                      {consumers.length > 0 ? (
                        consumers.map((consumer: any, index: number) => (
                          <Popup
                            key={index}
                            trigger={<span className="model-tag clickable">{consumer.name}</span>}
                            position="top center"
                            on="click"
                            className="model-popup"
                          >
                            <div className="model-popup-content">
                              
                              {agentDescriptions[consumer.name] && (
                                <div className="model-popup-section">
                                  <div className="model-popup-label">Description</div>
                                  <div className="model-popup-description">{agentDescriptions[consumer.name]}</div>
                                </div>
                              )}
                              
                            </div>
                          </Popup>
                        ))
                      ) : (
                        <span className="model-tag">No consumers</span>
                      )}
                    </div>
                  </div>

                  {/* Schema Overview */}
                  <div className="info-subsection">
                    <h4 className="info-subsection-title">Schema Overview ({schemaFields.length} fields)</h4>
                    <div className="schema-fields">
                      {schemaFields.length > 0 ? (
                        schemaFields.slice(0, 4).map((field: any, index: number) => (
                          <div key={index} className="schema-field">
                            <span className="field-name">{field.field_name}</span>
                            <span className="field-type">({field.data_type})</span>
                            { <span className="field-required" style={{ color: '#FF6467' }}>*</span>}
                          </div>
                        ))
                      ) : (
                        <div className="schema-field">
                          <span className="field-name">No schema data available</span>
                        </div>
                      )}
                      {schemaFields.length > 4 && (
                        <div className="schema-field-more">
                          +{schemaFields.length - 4} more fields
                        </div>
                      )}
                    </div>
                  </div>

                  {/* View File Details Link - Updated to use the new component */}
                  <div className="view-details-link" onClick={() => setShowFileDetails(true)}>
                    <span>View File Details</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}

export default SourceDetailView